// services/CrewService.js
import axios from 'axios';
import { ROUTE_CONFIG } from '../config/routeConfig';
import { getCrewColor } from '../utils/crewUtils';

export class CrewService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: ROUTE_CONFIG.API.TIMEOUT,
    });
  }
  
  /**
   * Process crews data with colors and formatting
   */
  processCrews(crews, entityId = "640a56404965b71a9edd0e7d") {
    if (!crews || !Array.isArray(crews)) return [];
    
    // Filter HMI crews (they're already filtered in mapStateToProps, but double-check)
    const hmiCrews = crews.filter(crew => 
      !crew.entity || crew.entity === entityId
    );
    
    // Remove duplicates and add colors
    const uniqueCrewIds = [...new Set(hmiCrews.map(crew => crew._id))];
    
    return uniqueCrewIds.map((crewId, index) => {
      const crew = hmiCrews.find(c => c._id === crewId);
      if (!crew) return null;
      
      const colorConfig = getCrewColor(index);
      
      return {
        ...crew,
        name: this.formatCrewName(crew),
        color: colorConfig.backgroundColor,
        textColor: colorConfig.textColor,
        index: index
      };
    }).filter(Boolean);
  }
  
  /**
   * Format crew name with foreman
   */
  formatCrewName(crew) {
    if (!crew) return 'Unassigned';
    return `${crew.name}${crew.foremanName ? ` (${crew.foremanName})` : ''}`;
  }
  
  /**
   * Calculate weekly hours for all crews
   */
  calculateWeeklyHours(weeklyRoutes, crews) {
    const weeklyHours = {};
    console.log('🔢 CrewService.calculateWeeklyHours called with:', {
      routesCount: weeklyRoutes?.length,
      crewsCount: crews?.length
    });
    
    // Initialize all crews
    crews.forEach(crew => {
      weeklyHours[crew._id] = {
        Monday: { serviceTime: 0, driveTime: 0 },
        Tuesday: { serviceTime: 0, driveTime: 0 },
        Wednesday: { serviceTime: 0, driveTime: 0 },
        Thursday: { serviceTime: 0, driveTime: 0 },
        Friday: { serviceTime: 0, driveTime: 0 },
        Saturday: { serviceTime: 0, driveTime: 0 },
        Sunday: { serviceTime: 0, driveTime: 0 },
        total: { serviceTime: 0, driveTime: 0 }
      };
    });
    
    // Calculate hours from routes
    weeklyRoutes.forEach(dayRoute => {
      const dayName = dayRoute.day;
      console.log(`  Processing ${dayName}:`);
      
      dayRoute.routes.forEach(route => {
        const crewId = route.crewId;
        if (!weeklyHours[crewId]) {
          console.warn(`    ⚠️ Skipping unknown crew: ${crewId}`);
          return;
        }
        
        const serviceMinutes = route.totalServiceTime || 0;
        const driveMinutes = route.totalDriveTime || 0;
        const serviceTime = serviceMinutes / 60; // Convert to hours
        const driveTime = driveMinutes / 60;
        
        console.log(`    Crew ${route.crewName || crewId}:`, {
          serviceMinutes,
          driveMinutes,
          serviceHours: serviceTime.toFixed(2),
          driveHours: driveTime.toFixed(2),
          stops: route.stops?.length || 0
        });
        
        weeklyHours[crewId][dayName].serviceTime += serviceTime;
        weeklyHours[crewId][dayName].driveTime += driveTime;
        weeklyHours[crewId].total.serviceTime += serviceTime;
        weeklyHours[crewId].total.driveTime += driveTime;
      });
    });
    // console.log('Weekly Hours...', weeklyHours)
    return weeklyHours;
  }
  
  /**
   * Get crew locations
   */
  async getCrewLocations(crewIds) {
    try {
      const response = await this.client.get('/api/hmi/genericGetRequest', {
        params: {
          type: 'crewLocations',
          crewIds: crewIds.join(',')
        }
      });
      
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to get crew locations:', error);
      return [];
    }
  }
  
  /**
   * Update crew assignment
   */
  async updateCrewAssignment(crewId, updates) {
    try {
      const response = await this.client.post('/api/hmi/genericPostRequest', {
        crewId,
        ...updates
      }, {
        params: { type: 'updateCrew' }
      });
      
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update crew: ${error.message}`);
    }
  }
  
  /**
   * Get crew availability for date
   */
  getCrewAvailability(crew, date, weeklyHours) {
    const dayOfWeek = moment(date).format('dddd');
    const dayData = weeklyHours[crew._id]?.[dayOfWeek] || { serviceTime: 0, driveTime: 0 };
    const totalHours = dayData.serviceTime + dayData.driveTime;
    
    return {
      available: totalHours < ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS,
      currentHours: totalHours,
      remainingCapacity: Math.max(0, ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS - totalHours),
      canTakeJob: (jobHours) => totalHours + jobHours <= ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS
    };
  }
  
  /**
   * Find best available crew for a job
   */
  findBestCrewForJob(crews, job, date, weeklyHours) {
    const availableCrews = crews
      .map(crew => ({
        crew,
        availability: this.getCrewAvailability(crew, date, weeklyHours)
      }))
      .filter(({ availability }) => availability.canTakeJob(job.estimatedHours))
      .sort((a, b) => b.availability.remainingCapacity - a.availability.remainingCapacity);
    
    return availableCrews[0]?.crew || null;
  }
  
  /**
   * Get crew performance metrics
   */
  getCrewMetrics(crew, weeklyHours, weeklyRoutes) {
    const crewHours = weeklyHours[crew._id];
    if (!crewHours) return null;
    
    const totalHours = crewHours.total.serviceTime + crewHours.total.driveTime;
    const utilizationRate = (totalHours / (ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS * 5)) * 100;
    const efficiencyRate = totalHours > 0 ? (crewHours.total.serviceTime / totalHours) * 100 : 0;
    
    // Count total jobs
    let totalJobs = 0;
    weeklyRoutes.forEach(dayRoute => {
      const crewRoute = dayRoute.routes.find(r => r.crewId === crew._id);
      if (crewRoute) {
        totalJobs += crewRoute.stops.length;
      }
    });
    
    return {
      totalHours: Math.round(totalHours * 100) / 100,
      serviceHours: Math.round(crewHours.total.serviceTime * 100) / 100,
      driveHours: Math.round(crewHours.total.driveTime * 100) / 100,
      utilizationRate: Math.round(utilizationRate),
      efficiencyRate: Math.round(efficiencyRate),
      totalJobs,
      averageJobTime: totalJobs > 0 ? Math.round((crewHours.total.serviceTime / totalJobs) * 100) / 100 : 0
    };
  }
}